import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/context/SupabaseAuthContext';

function getMonthRange(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

function getWeekNumber(date) {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  return Math.floor((date.getDate() + firstDay.getDay() - 1) / 7) + 1;
}

function getWeekRanges(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks = [];
  
  let currentWeek = 1;
  let weekStart = 1;
  
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const testDate = new Date(year, month, day);
    const calculatedWeek = getWeekNumber(testDate);
    
    if (calculatedWeek > currentWeek) {
      weeks.push({
        week: currentWeek,
        start: weekStart,
        end: day - 1,
        label: `${new Date(year, month, weekStart).getDate()}-${new Date(year, month, day - 1).getDate()}`
      });
      currentWeek = calculatedWeek;
      weekStart = day;
    }
  }
  
  weeks.push({
    week: currentWeek,
    start: weekStart,
    end: lastDay.getDate(),
    label: weekStart === lastDay.getDate() ? 
      `${weekStart}` : 
      `${weekStart}-${lastDay.getDate()}`
  });
  
  return weeks;
}

const CarbonTrendChart: React.FC<{ month: Date; refreshTrigger?: number }> = ({ month, refreshTrigger }) => {
  const { user } = useSupabaseAuth();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const [start, end] = getMonthRange(month);
    
    supabase
      .from('carbon_logs')
      .select('date,total_co2')
      .eq('user_id', user.id)
      .gte('date', start)
      .lte('date', end)
      .then(({ data: logs }) => {
        const weekRanges = getWeekRanges(month.getFullYear(), month.getMonth());
        
        const weekTotals: { [week: number]: number } = {};
        (logs || []).forEach(log => {
          const d = new Date(log.date);
          const week = getWeekNumber(d);
          weekTotals[week] = (weekTotals[week] || 0) + (log.total_co2 || 0);
        });
        
        const chartData = weekRanges.map(weekRange => ({
          week: `Week ${weekRange.week}`,
          weekLabel: `${weekRange.label}`,
          total: Math.round(weekTotals[weekRange.week] || 0),
        }));
        
        setData(chartData);
        setLoading(false);
      });
  }, [user, month, refreshTrigger]);

  if (!user) return null;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="text-sm text-muted-foreground">Loading chart...</div>
      </div>
    );
  }
  
  if (!data.some(d => d.total > 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-2">
        <div className="text-sm text-muted-foreground">No emissions data for this month</div>
        <div className="text-xs text-muted-foreground">Start tracking to see your weekly trends</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Chart Container */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="currentColor" 
              opacity={0.1} 
              vertical={false}
            />
            <XAxis 
              dataKey="week" 
              tick={{ fontSize: 11, fill: 'currentColor' }} 
              axisLine={false}
              tickLine={false}
              tickFormatter={(value, index) => {
                const currentItem = data[index];
                if (currentItem && currentItem.weekLabel) {
                  const weekNum = currentItem.week.split(' ')[1];
                  return typeof window !== 'undefined' && window.innerWidth > 768 ? 
                    `Week ${weekNum}` : 
                    `W${weekNum}`;
                }
                return value;
              }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'currentColor' }} 
              width={35}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: any, name: any, props: any) => [
                `${value} kg CO₂e`, 
                `Days ${props.payload.weekLabel}`
              ]}
              labelFormatter={(label: any, payload: any) => 
                payload && payload[0] ? 
                  `${label}` : 
                  label
              }
            />
            <Bar 
              dataKey="total" 
              fill="#40e0d0" 
              radius={[4, 4, 0, 0]}
              maxBarSize={60}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Weekly Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {data.map((week, index) => (
          <div key={week.week} className="text-center space-y-1">
            <div className="text-xs text-muted-foreground">{week.week.replace('Week ', 'W')}</div>
            <div className="text-sm font-semibold text-foreground">{week.total} kg</div>
            <div className="text-xs text-muted-foreground">Days {week.weekLabel}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CarbonTrendChart; 